import React, { useCallback, useEffect, useId, useRef } from 'react';
import {
  CircularProgress,
  FormControl,
  IconButton,
  InputAdornment,
  InputBase,
  InputLabel,
  OutlinedInput,
  type InputBaseComponentProps,
  type SxProps,
  type Theme,
} from '@mui/material';
import MicNoneOutlinedIcon from '@mui/icons-material/MicNoneOutlined';
import StopCircleOutlinedIcon from '@mui/icons-material/StopCircleOutlined';

import { color, semantic } from '../theme/tokens';
import { useDictationContext } from './DictationProvider';
import { useDictation, type UseDictation } from './useDictation';

type FieldElement = HTMLInputElement | HTMLTextAreaElement;

export interface TextInputProps {
  value: string;
  /** Every change, typed or dictated. There is deliberately no `onChange`. */
  onValueChange(text: string): void;
  /** Stable code of this field (`chat.composer`): sent with a dictated clip. */
  field: string;
  /** What the field is for, in a few words; helps the transcriber. */
  hint?: string;
  /** Draw the mic (default true). Nothing is drawn without a DictationProvider. */
  voice?: boolean;
  multiline?: boolean;
  minRows?: number;
  maxRows?: number;
  placeholder?: string;
  type?: 'text' | 'search' | 'email' | 'tel';
  disabled?: boolean;
  readOnly?: boolean;
  autoFocus?: boolean;
  fullWidth?: boolean;
  size?: 'small' | 'medium';
  /**
   * `bare` (default) is MUI's InputBase: the site's className / sx draws it.
   * `outlined` is MUI's outlined field, with an optional floating `label`;
   * as on a TextField, className / sx then style its FormControl root.
   */
  variant?: 'bare' | 'outlined';
  label?: string;
  /** Hold the outlined label above the box even when empty. */
  shrinkLabel?: boolean;
  id?: string;
  className?: string;
  sx?: SxProps<Theme>;
  inputProps?: InputBaseComponentProps;
  inputRef?: React.Ref<FieldElement>;
  /** Drawn inside the field, before the mic (a site's own spinner). */
  endAdornment?: React.ReactNode;
  onKeyDown?: React.KeyboardEventHandler<FieldElement>;
  onFocus?: React.FocusEventHandler<FieldElement>;
  onBlur?: React.FocusEventHandler<FieldElement>;
}

/**
 * Put dictated `text` into `value` at the selection `start..end`, with one
 * space either side where the neighbouring character is not already a space.
 * A null selection (an email input has none) appends. `caret` is where the
 * caret goes: just after the inserted words.
 */
export function spliceTranscript(
  value: string,
  text: string,
  start: number | null,
  end: number | null,
): { value: string; caret: number } {
  const words = text.trim();
  if (start === null || end === null) {
    if (!words) return { value, caret: value.length };
    const lead = value && !/\s$/.test(value) ? ' ' : '';
    const next = value + lead + words;
    return { value: next, caret: next.length };
  }
  const from = Math.min(start, end, value.length);
  const to = Math.min(Math.max(start, end), value.length);
  if (!words) return { value, caret: to };
  const before = value.slice(0, from);
  const after = value.slice(to);
  const lead = before && !/\s$/.test(before) ? ' ' : '';
  // No space before what follows if it is a space already or closing punctuation.
  const trail = after && !/^[\s,.;:!?)]/.test(after) ? ' ' : '';
  return {
    value: before + lead + words + trail + after,
    caret: before.length + lead.length + words.length,
  };
}

function setRef<T>(ref: React.Ref<T> | undefined, node: T | null) {
  if (typeof ref === 'function') ref(node);
  else if (ref) (ref as React.MutableRefObject<T | null>).current = node;
}

function MicButton({ dictation, disabled }: { dictation: UseDictation; disabled?: boolean }) {
  const { state, error } = dictation;
  const recording = state === 'recording';
  const transcribing = state === 'transcribing';
  return (
    <IconButton
      size="small"
      edge="end"
      aria-label={recording ? 'Stop dictating' : transcribing ? 'Transcribing' : 'Dictate'}
      title={error ?? undefined}
      disabled={disabled || transcribing}
      // Keep the caret in the field: the words land where it is.
      onMouseDown={(event) => event.preventDefault()}
      onClick={() => (recording ? dictation.stop() : void dictation.start())}
      sx={{
        // Compact, so the mic never makes the field taller than its text.
        p: 0.25,
        color: recording ? color.cobalt : error ? semantic.error : color.neutral600,
        '&.Mui-disabled': { color: color.neutral400 },
      }}
    >
      {transcribing ? (
        <CircularProgress size={18} sx={{ color: color.cobalt }} />
      ) : recording ? (
        <StopCircleOutlinedIcon fontSize="small" />
      ) : (
        <MicNoneOutlinedIcon fontSize="small" />
      )}
    </IconButton>
  );
}

/**
 * The one component for free text, single-line or multiline. Changes reach
 * the site only through `onValueChange`; under a `DictationProvider` the
 * field carries a mic that records, transcribes and splices the words in at
 * the caret.
 */
export default function TextInput({
  value,
  onValueChange,
  field,
  hint,
  voice = true,
  multiline = false,
  minRows,
  maxRows,
  placeholder,
  type = 'text',
  disabled,
  readOnly,
  autoFocus,
  fullWidth,
  size,
  variant = 'bare',
  label,
  shrinkLabel,
  id,
  className,
  sx,
  inputProps,
  inputRef,
  endAdornment,
  onKeyDown,
  onFocus,
  onBlur,
}: TextInputProps) {
  const provider = useDictationContext();
  const withMic = voice && provider !== null;

  const element = useRef<FieldElement | null>(null);
  const valueNow = useRef(value);
  valueNow.current = value;
  const pendingCaret = useRef<number | null>(null);

  const attach = useCallback(
    (node: FieldElement | null) => {
      element.current = node;
      setRef(inputRef, node);
    },
    [inputRef],
  );

  const onTranscript = useCallback(
    (text: string) => {
      const node = element.current;
      let start: number | null = null;
      let end: number | null = null;
      try {
        start = node?.selectionStart ?? null;
        end = node?.selectionEnd ?? null;
      } catch {
        // Some input types throw rather than answer null.
      }
      const spliced = spliceTranscript(valueNow.current, text, start, end);
      pendingCaret.current = start === null ? null : spliced.caret;
      onValueChange(spliced.value);
      // Back to the field only if focus is still in it (or on its mic) or
      // nowhere: someone who clicked elsewhere while it transcribed keeps their place.
      const active = typeof document === 'undefined' ? null : document.activeElement;
      const inField = active !== null && !!node?.closest('.MuiInputBase-root')?.contains(active);
      if (node && (active === null || active === document.body || inField)) node.focus();
    },
    [onValueChange],
  );

  const dictation = useDictation({ field, hint, onTranscript });
  const busy = dictation.state !== 'idle';

  // Once the spliced value is in the element, put the caret after the words.
  useEffect(() => {
    const caret = pendingCaret.current;
    const node = element.current;
    if (caret === null || !node || node.value !== value) return;
    pendingCaret.current = null;
    try {
      node.setSelectionRange(caret, caret);
    } catch {
      // No selection on this input type.
    }
  }, [value]);

  const fallbackId = useId();
  const inputId = id ?? (label ? fallbackId : undefined);

  const shared = {
    id: inputId,
    value,
    onChange: (event: React.ChangeEvent<FieldElement>) => onValueChange(event.target.value),
    multiline,
    minRows,
    maxRows,
    placeholder: dictation.state === 'recording' ? 'Listening…' : placeholder,
    type: multiline ? undefined : type,
    disabled,
    // Not disabled while busy: the field keeps its look, only typing stops.
    readOnly: readOnly || busy,
    autoFocus,
    fullWidth,
    size,
    inputProps,
    inputRef: attach,
    onKeyDown: (event: React.KeyboardEvent<FieldElement>) => {
      // While dictating, Enter must not submit a half-dictated message.
      if (busy && event.key === 'Enter') {
        event.preventDefault();
        return;
      }
      onKeyDown?.(event);
    },
    onFocus,
    // InputBase also calls onBlur with no event when a focused field becomes
    // disabled; the contract's handler always gets one.
    onBlur: onBlur && ((event?: React.FocusEvent<FieldElement>) => event && onBlur(event)),
    endAdornment:
      withMic || endAdornment ? (
        <InputAdornment
          position="end"
          sx={multiline ? { alignSelf: 'flex-end', height: 'auto' } : { gap: 0.5 }}
        >
          {endAdornment}
          {withMic && <MicButton dictation={dictation} disabled={disabled} />}
        </InputAdornment>
      ) : undefined,
  };

  if (variant === 'outlined') {
    return (
      <FormControl className={className} sx={sx} size={size} fullWidth={fullWidth} disabled={disabled}>
        {label && (
          <InputLabel htmlFor={inputId} shrink={shrinkLabel || undefined}>
            {label}
          </InputLabel>
        )}
        <OutlinedInput {...shared} label={label} notched={label && shrinkLabel ? true : undefined} />
      </FormControl>
    );
  }
  return <InputBase {...shared} className={className} sx={sx} />;
}
